// DEPLOY-PROBE-bump

/**
 * HomePage Component
 * 
 * Main application container that provides the layout structure for authenticated admin users.
 * This component manages the overall application layout including sidebar navigation,
 * header, and main content area with responsive design support.
 * 
 * Features:
 * - Responsive layout that adapts to mobile and desktop screens
 * - Collapsible sidebar navigation
 * - Dynamic content rendering based on selected page
 * - Mobile-first responsive design with automatic sidebar management
 * - Scroll-to-top behavior on page navigation and component mount
 * 
 * Layout Structure:
 * - Mobile: Vertical stack (Header -> Sidebar -> Content)
 * - Desktop: Horizontal arrangement (Sidebar | Header + Content)
 * 
 * @component
 */

import React, { useState, useEffect, useMemo } from 'react';
import { SideBar } from '../SidebarAndHeader/SideBar';
import {Header} from '../SidebarAndHeader/Header';
import { SalaryDataMain } from '../SalaryDataUploadDownload/SalaryDataMain';
import { UserManagementMain } from '../UserManagement/UserManagementMain';
import { PaySlipPreview } from '../PaySlipPreview/PaySlipPreview';
import { ConsentMain } from '../consent/ConsentMain';
import { ConsentFormSettings } from '../consent/ConsentFormSettings';
import { MyPayslips } from '../MyPayslips/MyPayslips';
import { AdminAccountSettings } from '../AccountSettings/AdminAccountSettings';
import { SecurityMain } from '../security/SecurityMain';
import { DocumentDeliveryMain } from '../DocumentDelivery/DocumentDeliveryMain';
import { SecurityPasswordGateModal } from '../security/components/SecurityPasswordGateModal';
import * as SecurityService from '../../services/SecurityService';
import EmployeeService from '../../services/EmployeeService';
import { getConsentFormConfig, getConsentStatus, submitConsent } from '../../services/ConsentService';
import { ConsentModal } from '../../components/FirstLoginConsent/ConsentModal';
import type { ResolvedConsentFormConfig } from '../../components/FirstLoginConsent/consentFormTypes';
import { buildLocalFallbackConsentConfig } from '../../components/FirstLoginConsent/consentDefaults';

/**
 * Props interface for HomePage component
 */
interface HomePageProps {
  employeeId: string;   // Current authenticated user's employee ID
  userType?: string;   // '管理者' (full) or '現場管理者' (construction-only)
  onLogout: () => void; // Callback function to handle user logout
}

export const HomePage: React.FC<HomePageProps> = ({ employeeId, userType, onLogout }) => {
  const isConstructionAdminOnly = userType === '現場管理者';
  const isAssignedSiteAdmin = userType === '担当現場管理者';
  const isLevel1Admin = userType === '管理者';
  const canDeliverDocuments = isLevel1Admin || isConstructionAdminOnly;
  const [activePage, setActivePage] = useState(() =>
    userType === '担当現場管理者' ? 'payroll' : 'salarydata'
  );

  const forcedAssignedGenba = useMemo(() => {
    if (userType !== '担当現場管理者') return undefined;
    try {
      const raw = sessionStorage.getItem('currentEmployee');
      if (!raw) return undefined;
      const p = JSON.parse(raw) as { assignedGenbaName?: string };
      const g = p.assignedGenbaName?.trim();
      return g || undefined;
    } catch {
      return undefined;
    }
  }, [userType]);
  
  // Controls sidebar collapsed/expanded state
  // Collapsed sidebar shows icons only, expanded shows full navigation labels
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  
  // Tracks whether the current device is mobile-sized (width <= 768px)
  const [isMobile, setIsMobile] = useState(false);

  // Security UI: show password gate when user clicks セキュリティ (Level 1 admin only)
  const [showSecurityPasswordModal, setShowSecurityPasswordModal] = useState(false);
  const [securityGateSubmitting, setSecurityGateSubmitting] = useState(false);
  const [securityGateError, setSecurityGateError] = useState<string | undefined>(undefined);

  /** 管理者・現場管理者: first-login 電子交付同意（本社 office として記録） */
  const [consentIdentity, setConsentIdentity] = useState<{ empCode: string; name: string } | null>(null);
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [consentSubmitting, setConsentSubmitting] = useState(false);
  const [consentFormConfig, setConsentFormConfig] = useState<ResolvedConsentFormConfig | null>(null);

  useEffect(() => {
    const raw = sessionStorage.getItem('currentEmployee');
    if (!raw) return;
    try {
      const p = JSON.parse(raw) as { empCode?: string; name?: string };
      if (!p?.empCode) return;
      const name = (p.name && String(p.name).trim()) || p.empCode;
      setConsentIdentity({ empCode: p.empCode, name });
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    if (!consentIdentity) return;
    let cancelled = false;
    getConsentStatus(consentIdentity.empCode, 'office')
      .then((data) => {
        if (!cancelled) {
          setShowConsentModal(Boolean(data.hasConsented === false));
        }
      })
      .catch(() => {
        if (!cancelled) setShowConsentModal(false);
      });
    return () => {
      cancelled = true;
    };
  }, [consentIdentity]);

  useEffect(() => {
    if (!consentIdentity) return;
    let cancelled = false;
    getConsentFormConfig()
      .then((c) => {
        if (cancelled) return;
        if (!c) {
          setConsentFormConfig(buildLocalFallbackConsentConfig());
          return;
        }
        setConsentFormConfig({
          sections: c.sections,
          printNoticeLines:
            c.printNoticeLines.length >= 2 ? [c.printNoticeLines[0], c.printNoticeLines[1]] : buildLocalFallbackConsentConfig().printNoticeLines,
        });
      })
      .catch(() => {
        if (!cancelled) setConsentFormConfig(buildLocalFallbackConsentConfig());
      });
    return () => {
      cancelled = true;
    };
  }, [consentIdentity]);

  /**
   * Effect hook to detect and respond to device screen size changes.
   * 
   * This ensures the layout adapts appropriately for mobile devices:
   * - Automatically detects mobile screens (width <= 768px)
   * - Updates mobile state on window resize events
   * - Scrolls to top when component mounts for consistent UX
   * - Cleans up event listener on component unmount to prevent memory leaks
   */
  useEffect(() => {
    /**
     * Checks if current viewport width is mobile-sized (<= 768px).
     * Updates the isMobile state accordingly.
     */
    const checkMobile = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
    };
    
    // Check initial screen size on component mount
    checkMobile();
    
    // Listen for window resize events to handle dynamic screen size changes
    window.addEventListener('resize', checkMobile);
    
    /**
     * Scroll to top when component mounts.
     * Uses multiple methods for cross-browser compatibility:
     * - window.scrollTo (modern browsers)
     * - document.documentElement.scrollTop (Chrome, Firefox, Opera)
     * - document.body.scrollTop (Safari)
     */
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    
    // Cleanup: remove event listener on component unmount
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  /** レベル1管理者はサイドバーにアカウント設定を出さない（従業員管理で自分を編集）。 */
  useEffect(() => {
    if (isLevel1Admin && activePage === 'account') {
      setActivePage('employee');
    }
  }, [isLevel1Admin, activePage]);

  /**
   * Handles navigation between different application pages
   * 
   * @param page - String identifier for the target page
   * 
   * This function:
   * - Updates the active page state
   * - Automatically collapses sidebar on mobile after navigation for better UX
   */
  const handlePageChange = (page: string) => {
    setActivePage(page);
    // Auto-collapse sidebar on mobile after navigation for better mobile experience
    if (isMobile) {
      setIsSidebarCollapsed(true);
    }
    // Scroll to top when changing pages
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  };

  /**
   * Toggles the sidebar between collapsed and expanded states
   * 
   * This provides users with control over the navigation area visibility,
   * especially useful on smaller screens or when more content space is needed.
   */
  const toggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  const handleSecurityNavClick = () => {
    setShowSecurityPasswordModal(true);
  };

  const handleSecurityGateConfirm = async (password: string) => {
    setSecurityGateError(undefined);
    setSecurityGateSubmitting(true);
    try {
      const res = await SecurityService.verifyPassword(password);
      if (res.success) {
        setShowSecurityPasswordModal(false);
        setActivePage('security');
        if (isMobile) setIsSidebarCollapsed(true);
        window.scrollTo(0, 0);
      } else {
        setSecurityGateError(res.message || 'パスワードが正しくありません。');
      }
    } catch {
      setSecurityGateError('確認に失敗しました。');
    } finally {
      setSecurityGateSubmitting(false);
    }
  };

  const resolvedConsentConfig = consentFormConfig ?? buildLocalFallbackConsentConfig();

  const handleConsentAgree = async () => {
    if (!consentIdentity || consentSubmitting) return;
    setConsentSubmitting(true);
    try {
      const ok = await submitConsent({
        empCode: consentIdentity.empCode,
        name: consentIdentity.name,
        empType: 'office',
        status: 'agree',
      });
      if (ok.success) setShowConsentModal(false);
    } finally {
      setConsentSubmitting(false);
    }
  };

  const handleConsentDisagree = async () => {
    if (!consentIdentity || consentSubmitting) return;
    setConsentSubmitting(true);
    try {
      const ok = await submitConsent({
        empCode: consentIdentity.empCode,
        name: consentIdentity.name,
        empType: 'office',
        status: 'disagree',
      });
      if (ok.success) {
        EmployeeService.logout();
        window.location.href = '/login';
      }
    } finally {
      setConsentSubmitting(false);
    }
  };

  /**
   * Renders the main content based on the currently selected page.
   * 
   * This function provides a centralized way to manage which content
   * is displayed based on user navigation choices.
   * 
   * Available pages:
   * - 'salarydata': Salary data upload/download management
   * - 'employee': User/employee management interface
   * - 'payroll': Payslip preview and generation
   * 
   * @returns JSX.Element - The appropriate component for the active page
   */
  const renderMainContent = () => {
    switch (activePage) {
      case 'salarydata':
        return <SalaryDataMain isConstructionAdminOnly={isConstructionAdminOnly} />;
      case 'employee':
        return <UserManagementMain isConstructionAdminOnly={isConstructionAdminOnly} />;
      case 'payroll':
        return (
          <PaySlipPreview
            hideOfficePayslipTab={isConstructionAdminOnly || isAssignedSiteAdmin}
            forcedAssignedGenba={forcedAssignedGenba}
            hideBonusTab={isAssignedSiteAdmin}
          />
        );
      case 'documents':
        return <DocumentDeliveryMain />;
      case 'myPayslips':
        return <MyPayslips employeeId={employeeId} />;
      case 'account':
        if (isLevel1Admin) {
          return <UserManagementMain isConstructionAdminOnly={isConstructionAdminOnly} />;
        }
        return <AdminAccountSettings />;
      case 'consent':
        return (
          <ConsentMain
            onOpenFormSettings={
              isLevel1Admin ? () => setActivePage('consentSettings') : undefined
            }
          />
        );
      case 'consentSettings':
        return <ConsentFormSettings onBack={() => setActivePage('consent')} />;
      case 'security':
        return <SecurityMain />;
      default:
        return <SalaryDataMain isConstructionAdminOnly={isConstructionAdminOnly} />;
    }
  };

  /**
   * Mobile Layout Configuration
   * 
   * On mobile devices (width <= 768px), the layout stacks vertically:
   * - Header at the top
   * - Collapsible sidebar below header
   * - Main content area at the bottom
   * 
   * This provides optimal use of limited screen space on mobile devices.
   */
  if (isMobile) {
    return (
      <div className="admin-layout mobile-layout">
        {showConsentModal && consentIdentity && (
          <ConsentModal
            empCode={consentIdentity.empCode}
            name={consentIdentity.name}
            config={resolvedConsentConfig}
            onAgree={handleConsentAgree}
            onDisagree={handleConsentDisagree}
            submitting={consentSubmitting}
          />
        )}
        {/* Header at the top for mobile layout */}
        <Header employeeId={employeeId} userType={userType} onLogout={onLogout} />
        
        {/* Collapsible sidebar below header */}
        {/* Sidebar can be toggled to expand/collapse for navigation */}
        <SideBar 
          activePage={activePage === 'consentSettings' ? 'consent' : activePage} 
          setActivePage={handlePageChange} 
          isCollapsed={isSidebarCollapsed}
          toggleSidebar={toggleSidebar}
          isMobile={isMobile}
          isConstructionAdminOnly={isConstructionAdminOnly}
          isAssignedSiteAdminOnly={isAssignedSiteAdmin}
          showAccountSettingsNav={!isLevel1Admin}
          showSecurityNav={isLevel1Admin}
          showDocumentsNav={canDeliverDocuments}
          onSecurityClick={handleSecurityNavClick}
        />
        
        {/* Main content area below sidebar */}
        <div className="mobile-content-area">
          {renderMainContent()}
        </div>
        <SecurityPasswordGateModal
          isOpen={showSecurityPasswordModal}
          onClose={() => { setShowSecurityPasswordModal(false); setSecurityGateError(undefined); }}
          onConfirm={handleSecurityGateConfirm}
          submitting={securityGateSubmitting}
          apiError={securityGateError}
        />
      </div>
    );
  }

  /**
   * Desktop Layout Configuration
   * 
   * On desktop devices, the layout uses a horizontal arrangement:
   * - Fixed sidebar on the left (can be collapsed)
   * - Main container on the right with dynamic margin:
   *   - Collapsed sidebar: 72px margin
   *   - Expanded sidebar: 282px margin
   * - Header at the top of main container
   * - Content area below header
   * 
   * The main container margin adjusts smoothly with a 0.3s transition
   * when the sidebar is toggled to provide a polished user experience.
   */
  return (
    <div className="admin-layout">
      {showConsentModal && consentIdentity && (
        <ConsentModal
          empCode={consentIdentity.empCode}
          name={consentIdentity.name}
          config={resolvedConsentConfig}
          onAgree={handleConsentAgree}
          onDisagree={handleConsentDisagree}
          submitting={consentSubmitting}
        />
      )}
      {/* Fixed sidebar on the left side */}
      {/* Sidebar position is fixed and remains visible during scrolling */}
      <SideBar 
        activePage={activePage === 'consentSettings' ? 'consent' : activePage} 
        setActivePage={handlePageChange} 
        isCollapsed={isSidebarCollapsed}
        toggleSidebar={toggleSidebar}
        isMobile={isMobile}
        isConstructionAdminOnly={isConstructionAdminOnly}
        isAssignedSiteAdminOnly={isAssignedSiteAdmin}
        showAccountSettingsNav={!isLevel1Admin}
        showSecurityNav={isLevel1Admin}
        showDocumentsNav={canDeliverDocuments}
        onSecurityClick={handleSecurityNavClick}
      />
      
      {/* Main container that adjusts based on sidebar state */}
      {/* Margin dynamically changes when sidebar is collapsed/expanded for smooth transition */}
      <div 
        className={`main-container ${isSidebarCollapsed ? 'sidebar-collapsed' : ''}`}
        style={{ 
          // Dynamic margin based on sidebar state (with 2px gap for visual separation)
          marginLeft: isSidebarCollapsed ? '72px' : '282px',
          // Smooth transition animation when sidebar state changes
          transition: 'margin-left 0.3s ease'
        }}
      >
        {/* Header at the top of the main content area */}
        <Header employeeId={employeeId} userType={userType} onLogout={onLogout} />
        
        {/* Main content area below header */}
        <div className="content-area">
          {renderMainContent()}
        </div>
      </div>
      <SecurityPasswordGateModal
        isOpen={showSecurityPasswordModal}
        onClose={() => { setShowSecurityPasswordModal(false); setSecurityGateError(undefined); }}
        onConfirm={handleSecurityGateConfirm}
        submitting={securityGateSubmitting}
        apiError={securityGateError}
      />
    </div>
  );
}

